import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart3,
  Settings,
  Rocket,
  Smile,
  Target,
  TrendingUp,
  Users,
  Globe,
  ShoppingCart,
  CheckCircle2,
  Award,
  Zap,
  Clock,
  Star,
  Heart,
  MessageSquare,
  Wrench,
  Package,
  Gauge,
  ThumbsUp,
  Pencil,
  Plus,
  X,
  Check,
  CalendarClock,
  TrendingDown,
  User,
} from "lucide-react";
import { getProdStats, saveProdStats } from "../../actions/prodStats.actions";

/* Icônes disponibles pour chaque chiffre (lucide-react, jamais d'emoji) */
const ICONS = {
  BarChart3,
  Settings,
  Rocket,
  Smile,
  Target,
  TrendingUp,
  Users,
  Globe,
  ShoppingCart,
  CheckCircle2,
  Award,
  Zap,
  Clock,
  Star,
  Heart,
  MessageSquare,
  Wrench,
  Package,
  Gauge,
  ThumbsUp,
};
const ICON_NAMES = Object.keys(ICONS);
const Icon = ({ name, ...rest }) => {
  const Cmp = ICONS[name] || BarChart3;
  return <Cmp {...rest} />;
};

const formatNumber = (n) => Number(n || 0).toLocaleString("fr-FR");

/* ---- Date de dernière mise à jour des chiffres ---- */
const formatUpdatedDate = (date) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);

const formatUpdatedTime = (date) =>
  new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(date);

// Libellé relatif court : "aujourd'hui", "hier", "il y a 5 j", "il y a 3 sem."…
const relativeLabel = (date) => {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return "hier";
  if (days < 7) return `il y a ${days} j`;
  if (days < 30) return `il y a ${Math.round(days / 7)} sem.`;
  if (days < 365) return `il y a ${Math.round(days / 30)} mois`;
  return `il y a ${Math.round(days / 365)} an${days >= 730 ? "s" : ""}`;
};

// Dernière mise à jour : date la plus récente parmi les chiffres, et l'admin
// qui l'a enregistrée (renvoyé par l'API sous `updatedBy`).
const getLastUpdate = (stats) => {
  let latest = null;
  stats.forEach((s) => {
    const t = new Date(s.updatedAt || s.updated_at || s.createdAt || 0).getTime();
    if (!Number.isFinite(t) || t <= 0) return;
    if (!latest || t > latest.time) latest = { time: t, by: s.updatedBy || null };
  });
  if (!latest) return null;
  const by = latest.by;
  const author = by
    ? [by.firstname, by.name].filter(Boolean).join(" ").trim()
    : "";
  return { date: new Date(latest.time), author };
};

/* ---- Tendance d'un chiffre par rapport à sa valeur précédente ---- */
const getTrend = (stat) => {
  const prev = stat.previousValue ?? stat.previous_value;
  if (prev === null || prev === undefined) return null;
  const delta = Number(stat.value) - Number(prev);
  if (!Number.isFinite(delta) || delta === 0) return null;
  return { delta, prev: Number(prev), up: delta > 0 };
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---- Comptage animé, façon compteur qui grimpe (ease-out) ---- */
const useCountUp = (target, run) => {
  const [display, setDisplay] = useState(run ? 0 : target);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!run) {
      setDisplay(target);
      return;
    }
    if (prefersReducedMotion()) {
      setDisplay(target);
      return;
    }

    const DURATION = 1600;
    let start = null;
    const from = 0;
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / DURATION, 1);
      setDisplay(Math.round(from + (target - from) * ease(progress)));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, run]);

  return display;
};

const StatItem = ({ stat, run, delay }) => {
  const display = useCountUp(stat.value, run);
  const trend = getTrend(stat);
  return (
    <div className="prodstat" style={{ "--i": delay }}>
      <span className="prodstat-badge">
        <Icon name={stat.icon} size={30} color={stat.color || "#3b82f6"} />
      </span>
      <span className="prodstat-value-row">
        <span className="prodstat-value">
          {formatNumber(display)}
          {stat.suffix ? <span className="prodstat-suffix"> {stat.suffix}</span> : null}
        </span>
        {trend && (
          <span
            className={`prodstat-trend ${trend.up ? "is-up" : "is-down"}`}
            title={`${trend.up ? "En hausse" : "En baisse"} : ${formatNumber(trend.prev)} → ${formatNumber(stat.value)}`}
            aria-label={`${trend.up ? "En hausse" : "En baisse"} de ${formatNumber(Math.abs(trend.delta))} par rapport à la valeur précédente (${formatNumber(trend.prev)})`}
          >
            {trend.up ? (
              <TrendingUp size={13} aria-hidden="true" />
            ) : (
              <TrendingDown size={13} aria-hidden="true" />
            )}
            {trend.up ? "+" : "−"}
            {formatNumber(Math.abs(trend.delta))}
          </span>
        )}
      </span>
      <span className="prodstat-label">{stat.label}</span>
    </div>
  );
};

/* Particules de lumière montantes, reprises de la bannière existante */
const randBetween = (min, max) => min + Math.random() * (max - min);
const makeParticles = (count) =>
  Array.from({ length: count }, (_, i) => ({
    id: i,
    style: {
      "--x": `${randBetween(2, 98)}%`,
      "--size": `${randBetween(2, 5)}px`,
      "--rise": `${randBetween(220, 420)}px`,
      "--drift": `${randBetween(-30, 30)}px`,
      "--o": randBetween(0.25, 0.65),
      "--dur": `${randBetween(9, 18)}s`,
      "--delay": `${randBetween(0, 14)}s`,
    },
  }));

const emptyStat = () => ({
  label: "",
  value: 0,
  suffix: "",
  icon: "BarChart3",
  color: "#3b82f6",
});

const ProdStats = () => {
  const dispatch = useDispatch();
  const stats = useSelector((state) => state.prodStatsReducer);
  const userData = useSelector((state) => state.userReducer);
  const isAdmin = !!userData.isAdmin;

  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const sectionRef = useRef(null);
  const particles = useMemo(() => makeParticles(14), []);
  const lastUpdate = useMemo(() => getLastUpdate(stats), [stats]);

  useEffect(() => {
    dispatch(getProdStats());
  }, [dispatch]);

  // Déclenche le comptage quand la bannière entre dans le viewport
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const startEdit = () => {
    setDraft(
      (stats.length ? stats : []).map((s) => ({
        id: s.id,
        label: s.label,
        value: s.value,
        suffix: s.suffix || "",
        icon: s.icon || "BarChart3",
        color: s.color || "#3b82f6",
      }))
    );
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft([]);
  };

  const updateDraft = (index, field, value) =>
    setDraft((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );

  const addStat = () => setDraft((prev) => [...prev, emptyStat()]);
  const removeStat = (index) =>
    setDraft((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(saveProdStats(draft));
      setEditing(false);
      setDraft([]);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  if (!editing && stats.length === 0) return null;

  return (
    <section
      className="prodstats-banner"
      aria-label="Chiffres clés de la prod"
      ref={sectionRef}
    >
      <div className="prodstats-bg" aria-hidden="true" />

      <div className="prodstats-particles" aria-hidden="true">
        {particles.map((p) => (
          <span key={p.id} className="particle" style={p.style} />
        ))}
      </div>

      <div className="prodstats-inner">
        <div className="prodstats-head">
          <span className="prodstats-eyebrow">Chiffres clés</span>
          <h2 className="prodstats-title-gradient">La Prod' en un coup d'œil</h2>
          <span className="prodstats-underline" aria-hidden="true" />

          <div className="prodstats-meta">
            {isAdmin && !editing && (
              <button
                type="button"
                className="prodstats-edit-btn"
                onClick={startEdit}
              >
                <Pencil size={15} />
                Modifier
              </button>
            )}

            {lastUpdate && (
              <div
                className="prodstats-updated"
                title={`Dernière mise à jour le ${formatUpdatedDate(lastUpdate.date)} à ${formatUpdatedTime(lastUpdate.date)}${lastUpdate.author ? ` par ${lastUpdate.author}` : ""}`}
              >
                <span className="prodstats-updated-line">
                  <CalendarClock size={12} aria-hidden="true" />
                  <span>
                    Mis à jour{" "}
                    <time dateTime={lastUpdate.date.toISOString()}>
                      {relativeLabel(lastUpdate.date)}
                    </time>
                    <span className="prodstats-updated-date">
                      {" · "}
                      {formatUpdatedDate(lastUpdate.date)}
                    </span>
                  </span>
                </span>
                {lastUpdate.author && (
                  <span className="prodstats-updated-line">
                    <User size={12} aria-hidden="true" />
                    <span>
                      par <strong>{lastUpdate.author}</strong>
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {editing ? (
          <div className="prodstats-editor">
            <div className="prodstats-editor-list">
              {draft.map((s, i) => (
                <div className="prodstats-editor-row" key={i}>
                  <span
                    className="prodstats-editor-preview"
                    style={{ borderColor: `${s.color}55` }}
                  >
                    <Icon name={s.icon} size={22} color={s.color} />
                  </span>

                  <label className="prodstats-field prodstats-field-icon">
                    <span>Icône</span>
                    <select
                      value={s.icon}
                      onChange={(e) => updateDraft(i, "icon", e.target.value)}
                    >
                      {ICON_NAMES.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="prodstats-field prodstats-field-color">
                    <span>Couleur</span>
                    <input
                      type="color"
                      value={s.color}
                      onChange={(e) => updateDraft(i, "color", e.target.value)}
                    />
                  </label>

                  <label className="prodstats-field prodstats-field-value">
                    <span>Chiffre</span>
                    <input
                      type="number"
                      min="0"
                      value={s.value}
                      onChange={(e) => updateDraft(i, "value", e.target.value)}
                    />
                  </label>

                  <label className="prodstats-field prodstats-field-suffix">
                    <span>Suffixe</span>
                    <input
                      type="text"
                      placeholder="ex : Visites"
                      value={s.suffix}
                      onChange={(e) => updateDraft(i, "suffix", e.target.value)}
                    />
                  </label>

                  <label className="prodstats-field prodstats-field-label">
                    <span>Libellé</span>
                    <input
                      type="text"
                      placeholder="ex : Sites en cours"
                      value={s.label}
                      onChange={(e) => updateDraft(i, "label", e.target.value)}
                    />
                  </label>

                  <button
                    type="button"
                    className="prodstats-row-remove"
                    onClick={() => removeStat(i)}
                    aria-label="Supprimer ce chiffre"
                    title="Supprimer"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="prodstats-add-btn"
              onClick={addStat}
            >
              <Plus size={16} />
              Ajouter un chiffre
            </button>

            <div className="prodstats-editor-actions">
              <button
                type="button"
                className="prodstats-btn prodstats-btn-ghost"
                onClick={cancelEdit}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="prodstats-btn prodstats-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <Check size={16} />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        ) : (
          <div className="prodstats-grid">
            {stats.map((s, i) => (
              <StatItem key={s.id} stat={s} run={visible} delay={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProdStats;
